import React, { useMemo } from 'react';
import { Card, Table, Tag, Typography, Descriptions, Space } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const EVENT_META = {
    created: { color: 'blue', label: 'Создание' },
    items_added: { color: 'green', label: 'Привязка' },
    auto_added: { color: 'green', label: 'Привязка (авто)' },
    task_removed: { color: 'red', label: 'Отвязка заявки' },
    item_removed: { color: 'red', label: 'Отвязка строки' },
    auto_removed: { color: 'red', label: 'Отвязка (авто)' },
    invoice_deleted: { color: 'red', label: 'Строка удалена из ВДР' },
    status: { color: 'purple', label: 'Статус' },
    metadata: { color: 'gold', label: 'Реквизиты' }
};

/** Старые записи без details — определяем тип по тексту. */
const guessEvent = text => {
    const value = String(text || '').toLowerCase();
    if (value.includes('создан')) return 'created';
    if (value.includes('автоматически') && value.includes('отвяз')) return 'auto_removed';
    if (value.includes('автоматически') && value.includes('добав')) return 'auto_added';
    if (value.includes('удалена заявка') || value.includes('отвязана заявка')) return 'task_removed';
    if (value.includes('удалена строка') || value.includes('отвязан')) return 'item_removed';
    if (value.includes('добавлен') || value.includes('привязан')) return 'items_added';
    if (value.includes('статус')) return 'status';
    if (value.includes('реквизит')) return 'metadata';
    return null;
};

const parseDetails = value => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (_) {
        return null;
    }
};

const money = value =>
    Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const ITEM_COLUMNS = [
    {
        title: 'Заявка',
        dataIndex: 'external_id',
        width: 90,
        render: (value, row) => value || (row.task_id ? `#${row.task_id}` : '—')
    },
    {
        title: 'Строка ВДР',
        dataIndex: 'rate_name',
        ellipsis: { showTitle: true },
        render: (value, row) => (
            <span>
                {value || row.article || '—'}
                <Text type="secondary" style={{ fontSize: 11 }}>{` · ID ${row.id}`}</Text>
            </span>
        )
    },
    {
        title: 'Статья',
        dataIndex: 'article',
        width: 150,
        ellipsis: true,
        render: value => value || '—'
    },
    {
        title: 'Кол-во',
        dataIndex: 'quantity',
        width: 90,
        align: 'right',
        render: (value, row) =>
            value === null || value === undefined ? '—' : `${value}${row.unit ? ` ${row.unit}` : ''}`
    },
    {
        title: 'Сумма',
        dataIndex: 'amount',
        width: 120,
        align: 'right',
        render: value => (value === null || value === undefined ? '—' : `${money(value)} ₽`)
    }
];

const HistoryDetails = ({ details }) => {
    const items = Array.isArray(details.items) ? details.items : [];
    const tasks = Array.isArray(details.tasks) ? details.tasks : [];
    const changes = Array.isArray(details.changes) ? details.changes : [];
    const filters =
        details.filters && typeof details.filters === 'object'
            ? Object.entries(details.filters)
            : [];

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {changes.length > 0 && (
                <div>
                    {changes.map(change => (
                        <div key={change.label}>
                            <Text type="secondary">{change.label}: </Text>
                            <Text delete>{change.from}</Text>
                            {' → '}
                            <Text strong>{change.to}</Text>
                        </div>
                    ))}
                </div>
            )}

            {details.invoice && !items.length && (
                <div>
                    <Text type="secondary">Строка ВДР: </Text>
                    <Text>
                        {details.invoice.article || `ID ${details.invoice.id}`}
                        {details.invoice.amount !== undefined
                            ? ` — ${money(details.invoice.amount)} ₽`
                            : ''}
                    </Text>
                </div>
            )}

            {tasks.length > 0 && (
                <div>
                    <Text type="secondary">
                        Заявки ({tasks.length}
                        {details.lines !== undefined ? `, строк: ${details.lines}` : ''}
                        {details.amount !== undefined ? `, сумма: ${money(details.amount)} ₽` : ''}):
                    </Text>
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {tasks.map(task => (
                            <Tag key={`${task.task_id}-${task.external_id}`} style={{ margin: 0 }}>
                                {task.external_id || `#${task.task_id}`}
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    {` · ${task.lines} стр. · ${money(task.amount)} ₽`}
                                </Text>
                            </Tag>
                        ))}
                    </div>
                </div>
            )}

            {items.length > 0 && (
                <div>
                    <Text type="secondary">
                        Строки ВДР ({details.items_total || items.length}
                        {details.items_total > items.length ? `, показаны первые ${items.length}` : ''}):
                    </Text>
                    <Table
                        style={{ marginTop: 6, maxWidth: 1100 }}
                        size="small"
                        rowKey="id"
                        columns={ITEM_COLUMNS}
                        dataSource={items}
                        pagination={items.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                    />
                </div>
            )}

            {filters.length > 0 && (
                <Descriptions
                    style={{ maxWidth: 900 }}
                    size="small"
                    column={1}
                    bordered
                    styles={{ label: { width: 220 } }}
                    title={<Text type="secondary" style={{ fontWeight: 400 }}>Фильтры подбора заявок</Text>}
                >
                    {filters.map(([label, value]) => (
                        <Descriptions.Item key={label} label={label}>
                            {value}
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            )}
        </Space>
    );
};

const hasDetails = details =>
    Boolean(
        details &&
            ((Array.isArray(details.tasks) && details.tasks.length) ||
                (Array.isArray(details.items) && details.items.length) ||
                (Array.isArray(details.changes) && details.changes.length) ||
                details.invoice ||
                (details.filters && Object.keys(details.filters).length))
    );

const RegistryHistoryCard = ({ history }) => {
    const rows = useMemo(
        () =>
            (Array.isArray(history) ? history : []).map(row => {
                const details = parseDetails(row.details);
                return {
                    ...row,
                    details,
                    event: details?.event || guessEvent(row.action_text),
                    at: row.created_at || row.createdAt
                };
            }),
        [history]
    );

    const columns = [
        {
            title: 'Когда',
            dataIndex: 'at',
            width: 140,
            render: value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—')
        },
        {
            title: 'Кто',
            dataIndex: 'user',
            width: 200,
            render: (user, row) => user?.fio || (row.user_id ? `ID ${row.user_id}` : '—')
        },
        {
            title: 'Событие',
            dataIndex: 'event',
            width: 150,
            render: event => {
                const meta = EVENT_META[event];
                return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>Прочее</Tag>;
            }
        },
        {
            title: 'Что сделано',
            dataIndex: 'action_text'
        }
    ];

    return (
        <Card
            size="small"
            style={{ marginTop: 16 }}
            title={
                <Space>
                    <HistoryOutlined />
                    История документа
                    <Tag>{rows.length}</Tag>
                </Space>
            }
        >
            <Table
                size="small"
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
                locale={{ emptyText: 'Записей в истории пока нет' }}
                expandable={{
                    rowExpandable: row => hasDetails(row.details),
                    expandedRowRender: row => <HistoryDetails details={row.details} />
                }}
            />
        </Card>
    );
};

export default RegistryHistoryCard;
